import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { StorefrontFAQData } from '../../../api';
import { useLanguage } from '../../../utils/GeneralUtils';

interface FAQsSectionProps {
  faqs: StorefrontFAQData[];
}

export const FAQsSection = ({ faqs }: FAQsSectionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const language = useLanguage();
  const [expanded, setExpanded] = useState<string | false>(false);

  const handlePanelExpand = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  if (!faqs.length) {
    return null;
  }

  const useTwoColumns = !isSmallScreen && faqs.length > 6;
  const midPoint = Math.ceil(faqs.length / 2);
  const leftColumnFaqs = useTwoColumns ? faqs.slice(0, midPoint) : faqs;
  const rightColumnFaqs = useTwoColumns ? faqs.slice(midPoint) : [];

  const renderFaqItem = (faq: StorefrontFAQData, index: number) => (
    <Accordion
      key={faq.id || index}
      expanded={expanded === `panel${index}`}
      onChange={handlePanelExpand(`panel${index}`)}
      disableGutters
      sx={{
        backgroundColor: 'transparent',
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '&:not(:last-child)': {
          borderBottom: '1px solid #e0e0e0',
        },
      }}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          padding: isSmallScreen ? '16px 0' : '20px 0',
          minHeight: 'unset',
          '& .MuiAccordionSummary-content': { margin: 0 },
        }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {faq.question?.[language]}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: isSmallScreen ? '0 0 16px' : '0 0 24px' }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.7 }}
          dangerouslySetInnerHTML={{ __html: faq.answer?.[language] || '' }}
        />
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Grid container flexDirection="column" mb={isSmallScreen ? 6 : 10}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          marginBottom: isSmallScreen ? 3 : 4,
          textAlign: 'center',
        }}>
        {t('storefront.faq')}
      </Typography>
      <Grid
        container
        gap={useTwoColumns ? 4 : 0}
        sx={{ maxWidth: useTwoColumns ? 1200 : 800, margin: '0 auto', width: '100%' }}>
        <Grid container flexDirection="column" flex={1}>
          {leftColumnFaqs.map((faq, index) => renderFaqItem(faq, index))}
        </Grid>
        {useTwoColumns && (
          <Grid container flexDirection="column" flex={1}>
            {rightColumnFaqs.map((faq, index) => renderFaqItem(faq, index + midPoint))}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
